import type { TreeState } from './types';

const SYMPTOM_TREE_PREFIX = 'sx-';

function isSymptomTreeKey(key: string | undefined | null): key is string {
  return typeof key === 'string' && key.startsWith(SYMPTOM_TREE_PREFIX);
}

function normalizeSymptomKeys(keys: Array<string | undefined | null>): string[] {
  return Array.from(new Set(keys.filter(isSymptomTreeKey)));
}

function normalizeAnswerKeys(keys: Array<string | undefined | null>): string[] {
  return Array.from(
    new Set(
      keys.filter((key): key is string => typeof key === 'string' && key.length > 0),
    ),
  );
}

function isNonEmptyObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && Object.keys(value as Record<string, unknown>).length > 0;
}

export function buildMergedSelectedSymptomKeys({
  answerKeys,
  selectedSymptomTreeKeys,
}: {
  answerKeys: Array<string | undefined | null>;
  selectedSymptomTreeKeys: Array<string | undefined | null>;
}): string[] {
  return Array.from(
    new Set([...normalizeAnswerKeys(answerKeys), ...normalizeSymptomKeys(selectedSymptomTreeKeys)]),
  );
}

export function collectRemovedSymptomTreeKeys({
  previousAnswerKeys,
  selectedSymptomTreeKeys,
  nextSelectedKeys,
}: {
  previousAnswerKeys: Array<string | undefined | null>;
  selectedSymptomTreeKeys: Array<string | undefined | null>;
  nextSelectedKeys: Array<string | undefined | null>;
}): string[] {
  const previousMerged = normalizeSymptomKeys([...previousAnswerKeys, ...selectedSymptomTreeKeys]);
  const nextMerged = new Set(normalizeSymptomKeys(nextSelectedKeys));
  return previousMerged.filter((key) => !nextMerged.has(key));
}

export function removeSymptomTreeStatesInPlace(
  treeStates: TreeState[],
  symptomTreeKeysToRemove: Array<string | undefined | null>,
): void {
  const removalKeySet = new Set(normalizeSymptomKeys(symptomTreeKeysToRemove));
  if (removalKeySet.size === 0) {
    return;
  }

  for (let idx = treeStates.length - 1; idx >= 0; idx -= 1) {
    const treeKey = treeStates[idx]?.treeKey;
    if (treeKey && removalKeySet.has(treeKey)) {
      treeStates.splice(idx, 1);
    }
  }
}

export function removeSymptomAnswerKeysFromAllTreesInPlace(
  treeStates: TreeState[],
  symptomTreeKeysToRemove: Array<string | undefined | null>,
): void {
  const removalKeySet = new Set(normalizeSymptomKeys(symptomTreeKeysToRemove));
  if (removalKeySet.size === 0) {
    return;
  }

  treeStates.forEach((treeState) => {
    const { answerMap } = treeState;
    if (!answerMap) return;

    Object.entries(answerMap).forEach(([questionKey, answerState]) => {
      const currentKeys = Array.isArray(answerState?.answerKeys) ? answerState.answerKeys : null;
      if (!currentKeys) return;

      const nextKeys = currentKeys.filter((key) => !removalKeySet.has(key));
      if (nextKeys.length !== currentKeys.length) {
        answerMap[questionKey] = {
          ...answerState,
          answerKeys: nextKeys,
        };
      }
    });
  });
}

export function collectSymptomItemAnswersFromTreeStates({
  treeStates,
  selectedSymptomTreeKeys,
}: {
  treeStates: TreeState[];
  selectedSymptomTreeKeys: Array<string | undefined | null>;
}): Record<string, Record<string, unknown>> {
  const selectedKeySet = new Set(normalizeSymptomKeys(selectedSymptomTreeKeys));
  if (selectedKeySet.size === 0) {
    return {};
  }

  const mergedItemAnswers: Record<string, Record<string, unknown>> = {};

  treeStates.forEach((treeState) => {
    const answerMap = treeState?.answerMap;
    if (!answerMap) return;

    Object.values(answerMap).forEach((answerState) => {
      const itemAnswers = (answerState as unknown as { itemAnswers?: Record<string, unknown> })?.itemAnswers;
      if (!isNonEmptyObject(itemAnswers)) return;

      Object.entries(itemAnswers).forEach(([symptomKey, symptomAnswer]) => {
        if (!selectedKeySet.has(symptomKey) || !isNonEmptyObject(symptomAnswer)) {
          return;
        }

        const existing = mergedItemAnswers[symptomKey];
        if (!existing || Object.keys(existing).length === 0) {
          mergedItemAnswers[symptomKey] = symptomAnswer;
          return;
        }

        // Prefer the richer answer payload when duplicates exist across collectors.
        if (Object.keys(symptomAnswer).length > Object.keys(existing).length) {
          mergedItemAnswers[symptomKey] = symptomAnswer;
        }
      });
    });
  });

  return mergedItemAnswers;
}
